/**
 * FileUploadDialog — modal d'upload PDF (flow 3 temps).
 *
 *  1. l'utilisateur sélectionne un PDF (input file ou drag-drop)
 *  2. on POST /v1/files/uploads/initiate avec les méta
 *  3. on PUT le binaire vers la signed URL (pas via l'API)
 *  4. on POST /v1/files/uploads/:id/complete pour confirmer
 *
 * État géré localement : étape courante, fichier sélectionné, progression
 * (best-effort : pas d'événement progress sur fetch PUT, on affiche
 * "Téléversement…" sans pourcentage).
 *
 * Accessibilité :
 *  - role="dialog" + aria-modal=true + aria-labelledby
 *  - focus auto sur le bouton de fermeture à l'ouverture
 *  - Escape ferme (sauf pendant uploading/completing pour éviter d'orphelins)
 *  - backdrop click ferme (idem caveat)
 */

import { LIMITS } from "@kollectivia/shared";
import { type FileKind, FILE_KINDS } from "@kollectivia/shared/schemas/files.js";
import { type ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import {
  type FileRow,
  useCompleteFileUpload,
  useInitiateFileUpload,
} from "../../../lib/queries/files";
import { beginTenantRequest } from "../../../lib/tenantScope";
import { toast } from "../../../lib/toast";
import { uploadToBucket } from "../../../lib/uploadPdf";

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

export type FileUploadParent =
  | { project_id: string; submission_id?: never; company_id?: never }
  | { submission_id: string; project_id?: never; company_id?: never }
  | { company_id: string; project_id?: never; submission_id?: never };

export type FileUploadDialogProps = {
  open: boolean;
  onClose: () => void;
  parent: FileUploadParent;
  /**
   * Kinds proposés à l'utilisateur. Default : tous (FILE_KINDS).
   * Pour un upload sur un projet (avant qu'une soumission n'existe), on
   * recommande ['client-request', 'attachment'] — `submission` est plutôt
   * le PDF de cotation reçu d'un assureur, qui sera attaché à la submission.
   */
  allowedKinds?: readonly FileKind[];
  /**
   * Kind par défaut. Doit appartenir à `allowedKinds`. Default : premier
   * élément de allowedKinds.
   */
  defaultKind?: FileKind;
  /**
   * Callback succès — passe la row finale (status='ready').
   * Le parent peut invalider ses queries spécifiques ici.
   */
  onUploaded?: (file: FileRow) => void;
};

type DialogStep = "pick" | "uploading" | "completing" | "done" | "error";

// -----------------------------------------------------------------------------
// Composant
// -----------------------------------------------------------------------------

export function FileUploadDialog(props: FileUploadDialogProps) {
  const { open, onClose, parent, allowedKinds = FILE_KINDS, defaultKind, onUploaded } = props;
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);

  const titleId = useId();
  const fileInputId = useId();
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const initialKind = useMemo<FileKind>(() => {
    if (defaultKind && allowedKinds.includes(defaultKind)) return defaultKind;
    return allowedKinds[0] ?? "attachment";
  }, [allowedKinds, defaultKind]);

  const [step, setStep] = useState<DialogStep>("pick");
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<FileKind>(initialKind);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const initiate = useInitiateFileUpload();
  const complete = useCompleteFileUpload();

  const isBusy = step === "uploading" || step === "completing";

  // -- Reset state à chaque (ré)ouverture -------------------------------------
  useEffect(() => {
    if (open) {
      setStep("pick");
      setFile(null);
      setKind(initialKind);
      setErrorMessage(null);
      setDragOver(false);
      // Focus le bouton fermer à l'ouverture (a11y).
      queueMicrotask(() => closeBtnRef.current?.focus());
    }
  }, [open, initialKind]);

  // -- Escape pour fermer -----------------------------------------------------
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isBusy) {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, isBusy, onClose]);

  // -- Validation client (taille + mime) --------------------------------------
  const validateFile = useCallback(
    (f: File): string | null => {
      if (f.type && f.type !== "application/pdf") {
        return t("files.upload.errors.not_pdf");
      }
      if (!f.name.toLowerCase().endsWith(".pdf")) {
        return t("files.upload.errors.not_pdf");
      }
      const maxBytes = LIMITS.PDF_MAX_SIZE_MB * 1024 * 1024;
      if (f.size > maxBytes) {
        return t("files.upload.errors.too_large", {
          max: LIMITS.PDF_MAX_SIZE_MB,
        });
      }
      if (f.size === 0) {
        return t("files.upload.errors.empty");
      }
      return null;
    },
    [t],
  );

  const onPickFile = (f: File | null) => {
    setErrorMessage(null);
    if (!f) {
      setFile(null);
      return;
    }
    const err = validateFile(f);
    if (err) {
      setErrorMessage(err);
      setFile(null);
      return;
    }
    setFile(f);
  };

  // -- Submit (lance le flow 3 temps) ----------------------------------------
  const onSubmit = async () => {
    if (!file) return;

    setErrorMessage(null);
    const operation = beginTenantRequest(true);
    setStep("uploading");

    try {
      // 1. INITIATE
      const initResult = await initiate.mutateAsync({
        ...parent,
        kind,
        file_name: file.name,
        size_bytes: file.size,
        mime_type: "application/pdf",
      });

      // 2. PUT bucket
      operation.assertCurrent();
      await uploadToBucket({
        signal: operation.signal,
        url: initResult.upload.url,
        headers: initResult.upload.headers,
        body: file,
      });

      // 3. COMPLETE
      operation.assertCurrent();
      setStep("completing");
      const completed = await complete.mutateAsync({
        fileId: initResult.file.id,
        input: {},
      });

      operation.assertCurrent();
      setStep("done");
      toast.success(t("files.upload.success", { name: completed.file_name }));
      onUploaded?.(completed);
      // Petite latence pour que l'utilisateur voie le "Terminé".
      setTimeout(() => onClose(), 600);
    } catch (e) {
      const message =
        e instanceof ApiException
          ? getApiErrorMessage(e, lang) || t("errors.generic")
          : e instanceof Error
            ? e.message
            : t("errors.generic");
      setErrorMessage(message);
      setStep("error");
      toast.error(t("files.upload.failed", { message }));
    } finally {
      operation.finish();
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4 backdrop-blur-sm"
      onClick={(e) => {
        // Backdrop click — ferme uniquement si pas busy + clic exact backdrop.
        if (e.target === e.currentTarget && !isBusy) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-sm shadow-xl"
      >
        <header className="mb-4 flex items-start justify-between gap-3">
          <h2 id={titleId} className="text-lg font-semibold text-slate-900">
            {t("files.upload.title")}
          </h2>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={() => !isBusy && onClose()}
            disabled={isBusy}
            aria-label={t("common.cancel")}
            className="focus-visible:outline-brand-500 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-40"
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>

        {/* Sélecteur de kind */}
        <fieldset className="mb-4" disabled={isBusy}>
          <legend className="mb-1 text-xs text-slate-500">{t("files.upload.kind_label")}</legend>
          <div className="grid grid-cols-3 gap-2">
            {allowedKinds.map((k) => (
              <label
                key={k}
                className={
                  "flex cursor-pointer items-center justify-center rounded-md border px-2 py-1.5 text-xs transition " +
                  (kind === k
                    ? "border-brand-500 bg-brand-50 text-brand-800"
                    : "border-slate-200 text-slate-700 hover:bg-slate-50")
                }
              >
                <input
                  type="radio"
                  name="upload-kind"
                  value={k}
                  checked={kind === k}
                  onChange={() => setKind(k)}
                  className="sr-only"
                />
                {t(`files.kinds.${k}`)}
              </label>
            ))}
          </div>
        </fieldset>

        {/* Drop zone / file input */}
        <div
          onDragOver={(e) => {
            if (isBusy) return;
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            if (isBusy) return;
            e.preventDefault();
            setDragOver(false);
            const f = e.dataTransfer.files?.[0] ?? null;
            onPickFile(f);
          }}
          className={
            "mb-3 grid place-items-center rounded-lg border-2 border-dashed p-6 text-center transition " +
            (dragOver ? "border-brand-500 bg-brand-50" : "border-slate-200")
          }
        >
          {file ? (
            <FilePreview file={file} onClear={() => onPickFile(null)} disabled={isBusy} />
          ) : (
            <>
              <p className="text-sm text-slate-700">{t("files.upload.dropzone")}</p>
              <p className="mt-1 text-xs text-slate-500">
                {t("files.upload.size_hint", { max: LIMITS.PDF_MAX_SIZE_MB })}
              </p>
              <label
                htmlFor={fileInputId}
                className={
                  "bg-brand-600 hover:bg-brand-700 mt-3 inline-flex cursor-pointer items-center rounded-md px-3 py-1.5 text-sm font-medium text-white transition " +
                  (isBusy ? "pointer-events-none opacity-40" : "")
                }
              >
                {t("files.upload.choose_file")}
              </label>
              <input
                ref={fileInputRef}
                id={fileInputId}
                type="file"
                accept="application/pdf,.pdf"
                disabled={isBusy}
                className="sr-only"
                onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
              />
            </>
          )}
        </div>

        {/* Status / erreur */}
        {errorMessage && (
          <p
            role="alert"
            className="mb-3 rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-700"
          >
            {errorMessage}
          </p>
        )}
        {step === "uploading" && <StatusLine label={t("files.upload.uploading")} />}
        {step === "completing" && <StatusLine label={t("files.upload.completing")} />}
        {step === "done" && <StatusLine label={t("files.upload.done")} tone="success" />}

        {/* Actions */}
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => !isBusy && onClose()}
            disabled={isBusy}
            className="btn-ghost"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!file || isBusy || step === "done"}
            className="btn-primary"
          >
            {step === "uploading"
              ? t("files.upload.uploading")
              : step === "completing"
                ? t("files.upload.completing")
                : t("files.upload.submit")}
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sous-composants
// -----------------------------------------------------------------------------

function FilePreview({
  file,
  onClear,
  disabled,
}: {
  file: File;
  onClear: () => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const sizeLabel = formatBytes(file.size);
  return (
    <div className="flex w-full items-center gap-3 text-left">
      <div className="bg-brand-100 text-brand-800 grid h-10 w-10 shrink-0 place-items-center rounded text-xs font-bold">
        PDF
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-slate-800">{file.name}</p>
        <p className="text-xs text-slate-500">{sizeLabel}</p>
      </div>
      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        aria-label={t("files.upload.clear")}
        className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"
      >
        <span aria-hidden="true">×</span>
      </button>
    </div>
  );
}

function StatusLine({ label, tone = "info" }: { label: ReactNode; tone?: "info" | "success" }) {
  return (
    <p
      className={
        "flex items-center gap-2 rounded-md border p-2 text-sm " +
        (tone === "success"
          ? "border-green-200 bg-green-50 text-green-800"
          : "border-brand-200 bg-brand-50 text-brand-800")
      }
    >
      {tone === "info" && (
        <span
          aria-hidden="true"
          className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {tone === "success" && <span aria-hidden="true">✓</span>}
      {label}
    </p>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}
